import type { User } from "firebase/auth"; // Import the Firebase User type for better typing

import { validateUserApi } from "~/lib/api";
import type { FullApiResponseWrapper } from "~/lib/api/axios-client";
import { getAxiosClient } from "~/lib/api/axios-client";
import { logOut as firebaseLogOut } from "~/lib/firebase-auth";
import { keysToCamelCase } from "~/utils/caseTransform";
import {
  logout as logoutAction,
  setAuthLoading,
  setUserData,
  type UserData,
} from "./authSlice";
import type { AppDispatch } from "./store";

export const fetchUserDataWithFirebaseUid = async (
  firebaseUid: string,
): Promise<UserData> => {
  const client = getAxiosClient();
  try {
    const response = await client.get<FullApiResponseWrapper<UserData>>(
      `/api/v1/users/external/${firebaseUid}`,
    );

    const userData = keysToCamelCase<UserData>(response.data.body.results?.[0]);
    return userData;
  } catch (error: any) {
    // Ensure status is on the error object for easier access
    if (error?.response?.status) {
      (error as any).status = error.response.status;
    }
    throw error;
  }
};

export const fetchUserDataWithUserId = async (
  userId?: string,
): Promise<UserData> => {
  const client = getAxiosClient();
  try {
    const response = await client.get<FullApiResponseWrapper<UserData>>(
      `/api/v1/users/${userId}`,
    );

    const userData = keysToCamelCase<UserData>(response.data.body.results?.[0]);
    return userData;
  } catch (error: any) {
    // Ensure status is on the error object for easier access
    if (error?.response?.status) {
      (error as any).status = error.response.status;
    }
    throw error;
  }
};

export const refreshUserData = (
  userId?: string,
): ((dispatch: AppDispatch) => Promise<UserData>) => {
  return async (dispatch: AppDispatch): Promise<UserData> => {
    if (!userId) {
      throw new Error("userId is required");
    }

    try {
      const userData = await fetchUserDataWithUserId(userId);
      dispatch(setUserData(userData));
      return userData;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      throw error;
    }
  };
};

/**
 * Validates user access and fetches complete user data
 */
export const validateAndFetchUserData =
  (firebaseUid: string) => async (dispatch: AppDispatch) => {
    try {
      // First validate user access via backend
      try {
        await validateUserApi();
      } catch (validationError: any) {
        // If validation fails with 404, it means user doesn't exist yet - this is expected for new users
        const status =
          validationError?.status || validationError?.response?.status;
        if (status !== 404) {
          throw validationError;
        }
        // For 404, continue to try fetching user or creating new user
      }

      // Then fetch complete user data
      try {
        const userData = await fetchUserDataWithFirebaseUid(firebaseUid);
        dispatch(setUserData(userData));

        return userData;
      } catch (fetchError: any) {
        const status = fetchError?.status || fetchError?.response?.status;

        // Re-throw with status preserved
        const error = new Error(
          fetchError.message || "Failed to fetch user data",
        );
        (error as any).status = status;
        (error as any).response = fetchError.response;
        throw error;
      }
    } catch (error: any) {
      throw error;
    }
  };

export const handleAuthInitialization =
  (firebaseUser: User | null) =>
  async (dispatch: AppDispatch, getState: () => any) => {
    const currentState = getState().auth;

    if (currentState.isSigningIn) return;

    if (firebaseUser) {
      dispatch(setAuthLoading(true));

      try {
        await dispatch(validateAndFetchUserData(firebaseUser.uid) as any);
      } catch (error: any) {
        console.error("Error during authentication/profile fetch:", error);

        if (error?.response?.status !== 404) {
          await firebaseLogOut();
          dispatch(logoutAction());
        } else {
          console.warn("User not found in backend, likely in creation process");
          dispatch(setAuthLoading(false));
        }
      }
    } else {
      dispatch(setAuthLoading(true));
      dispatch(logoutAction());
    }
  };
